import { FINAL_ORDER_STATUSES, bookedLine, poundsFromPence, statusLine, type OrderStage, type SupplierOrderBlock, type SupplierOrderState } from '@/lib/chat/blocks/supplier-order';

/**
 * Mucka says how a supplier order ended (Tom, 3 Nov 2026: "nothing is coming
 * back to the chat to let Mucka know the payment has happened for it to say
 * something to me to confirm").
 *
 * The order card is the one place that sees the moment an order finishes. On
 * a call it hands the agent the outcome as a turn of the owner's, the way a
 * card tap does (card-decision-update.ts), so the agent says it out loud. In
 * typed chat it raises ORDER_ENDED_EVENT and the panel adds Mucka's line.
 * Directive-free: imported by the card and the panel both.
 */

/**
 * A new card per step (Tom, 3 Nov 2026: the chat still said "approve it" above
 * a card that had moved on to "placing"). When the order moves on, the card
 * raises ORDER_STAGE_EVENT with Mucka's line and a card for the new step; the
 * panel posts it once per order and step, and the earlier card shrinks to one
 * line.
 */
export const ORDER_STAGE_EVENT = 'mucka-supplier-order-stage';

export interface OrderStageDetail { orderId: string; stage: OrderStage; text: string; block: SupplierOrderBlock }

/** Which step a live state is at. */
export function orderStage(state: SupplierOrderState): OrderStage {
  if (FINAL_ORDER_STATUSES.has(state.status)) return 'done';
  if (state.status === 'awaiting_approval') return 'prepared';
  if (state.status === 'needs_answer') return 'question';
  if (state.question?.kind === 'bank_app') return 'bank';
  return 'placing';
}

/** Mucka's line for a step after the first. */
export function orderStageLine(stage: OrderStage, block: SupplierOrderBlock, state: SupplierOrderState): string {
  switch (stage) {
    case 'placing': return `Approved. I'm placing the order at ${block.merchant} now; it usually takes three or four minutes.`;
    case 'bank': return 'Your bank wants you to approve the payment in its app. Do it now: I carry on as soon as it confirms.';
    case 'question': return `The ${block.merchant} order needs an answer from you.`;
    case 'done': return orderEndedLine(block, state);
    case 'prepared': return '';
  }
}

/** Mucka's line in typed chat. */
export function orderEndedLine(block: SupplierOrderBlock, state: SupplierOrderState): string {
  switch (state.status) {
    case 'ordered': {
      const ref = state.merchantOrderId ? ` (order ${state.merchantOrderId})` : '';
      const paid = state.totalPence != null ? `, ${poundsFromPence(state.totalPence)} on the ${block.card}` : '';
      const next = block.fulfilment === 'collect'
        ? `${block.merchant} will email you when it is ready to collect.`
        : `${block.merchant} will email you the delivery details.`;
      const booked = bookedLine(state.booked);
      return `Done: ${block.merchant} has taken the order${ref}${paid}. ${next}${booked ? ` ${booked}` : ''}`;
    }
    case 'cancelled':
      return `The ${block.merchant} order is cancelled. Nothing was bought.`;
    case 'rehearsed':
      return `That was a rehearsal: it reached ${block.merchant}'s payment step and stopped, so nothing was bought.`;
    default:
      return `The ${block.merchant} order did not go through. ${statusLine(block, state)}`;
  }
}

/** The turn a live call is given: the fact, in brackets, like a card tap. */
export function orderEndedTurn(block: SupplierOrderBlock, state: SupplierOrderState): string {
  return `[The ${block.merchant} order has finished: ${statusLine(block, state)}]`;
}

/**
 * The steps that have their own card in this chat, as `<orderId>:<stage>`.
 * An earlier card shrinks to a line only once its successor exists, so a card
 * first opened at a later step (after a reload) stays the live one rather than
 * shrinking with nothing below it.
 */
const stageCards = new Set<string>();
export function noteStageCard(orderId: string, stage: OrderStage): void { stageCards.add(`${orderId}:${stage}`); }
export function hasStageCard(orderId: string, stage: OrderStage): boolean { return stageCards.has(`${orderId}:${stage}`); }
